import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  Street,
  StreetDetails,
  PokerAction,
  HandDetails,
  GameState,
  Summary,
  JackpotDetails,
  BuyIn,
  Payout,
  CardType,
  SeatPosition,
  Stats
} from "types";
import axios from "axios";
import { getBb } from "./number";
import { calculatePokerOdds } from "utils/poker-odds";
import { getConfig } from "./config";

export type Player = {
  balance: number;
  winnings: string;
  amount: number;
  holeCards: CardType[];
  equity: number;
  winType: string;
  action: string;
  time: number;
  seatPosition: SeatPosition;
};

type PlayerRecord = Record<string, Player>;

type State = {
  sessionId: string;
  gameId: string;
  returnUrl: string;
  id: string;
  gameState: string;
  date: string;
  buyIns: BuyIn[];
  buyIn: BuyIn;
  playerId: string;
  tournamentId: string;
  hands: HandDetails[];
  handCount: number;
  summary: Summary | undefined;
  jackpotDetails: JackpotDetails | undefined;
  players: PlayerRecord;
  street: Street;
  showHandId: boolean;
  showPrize: boolean;
  showBank: boolean;
  showBounty: boolean;
  hideState: number;
  currentId: string | undefined;
  skip: boolean;
  currencyCode: string;
  successEnd: boolean;
  tournamentSize: number;
  enableDebug: boolean;
  payouts: Payout[];
  alertMessage: string | undefined;
  stats: Stats | undefined;
  showScratchCard: boolean;
  skin: string;
  showRegConfirm: boolean;
  showInsufficient: boolean;
};

type Actions = {
  setGame: (gameId: string, sessionId: string) => void;
  setReturnUrl: (returnUrl: string) => void;
  setBuyIn: (buyIn: BuyIn) => void;
  setBet: () => Promise<boolean>;
  resetHand: (data: any) => void;
  setEnd: () => void;
  setStartingPlayer: () => void;
  nextHand: () => void;
  setPlayers: () => void;
  setHandId: () => void;
  playerCardShown: (id: string) => void;
  nextStreet: () => void;
  updateAction: () => void;
  updatePlayers: (dealerCards: CardType[]) => void;
  updateWinnings: () => void;
  updatePrize: () => void;
  updateBank: () => void;
  updateBounty: () => void;
  hideHand: () => void;
  updateBalance: () => void;
  skipHand: () => void;
  reset: () => void;
  getPayouts: () => void;
  setAlertMessage: (message?: string) => void;
  setShowScratchCard: (show: boolean) => void;
  toggleShowScratchCard: () => void;
  setShowRegConfirm: (show: boolean) => void;
  setShowInsufficient: (show: boolean) => void;
};

export const initialPlayer: Player = {
  balance: 0,
  winnings: "0",
  winType: "",
  amount: 0,
  holeCards: [],
  equity: -1,
  action: "",
  time: 0,
  seatPosition: 0,
};

export const initialState: State = {
  sessionId: "",
  gameId: "",
  returnUrl: "",
  id: "",
  gameState: "",
  date: "",
  buyIns: [],
  buyIn: { configId: "", buyIn: 0 },
  playerId: "",
  tournamentId: "",
  hands: [],
  handCount: -1,
  summary: undefined,
  jackpotDetails: undefined,
  players: {},
  street: Street.PreFlop,
  showHandId: false,
  showPrize: false,
  showBank: false,
  showBounty: false,
  hideState: 0,
  currentId: undefined,
  skip: false,
  currencyCode: "USD",
  successEnd: false,
  tournamentSize: 0,
  enableDebug: ['DEV', 'QA', 'STG'].includes(getConfig('REACT_APP_ENV') || ''),
  payouts: [],
  alertMessage: undefined,
  stats: undefined,
  showScratchCard: false,
  skin: '',
  showRegConfirm: false,
  showInsufficient: false,
};

const getDetails = (roundDetails: StreetDetails[], playerId: string) => {
  return [...roundDetails]
    .reverse()
    .find((stage: StreetDetails) => stage.playerId === playerId);
};

let _hasAllIn = false;
let _handCount = -1;
let _isLastHand = false;
let _allInPlayers: PlayerRecord;
let _isEndCalled = false;

export const useStore = create<State & Actions>()(
  devtools(
  persist(
    immer((set, get) => ({
      ...initialState,
      setGame: async (gameId, sessionId) => {
        set({ gameId, sessionId, gameState: initialState.gameState, skin: getConfig('REACT_APP_SKIN') });
        try {
          let data;

          // START_DEVTOOL
          // Set current data to localstorage for devtool bug reporting
          const storedData = window.localStorage.getItem("BH:Data");
          if (window.localStorage.getItem("BH:UseRecording") === "true" && storedData && get().enableDebug) {
            data = JSON.parse(storedData);
            console.log("=== Using Replay ===");
            console.log(data);
          } else {
            const response = await axios.post(
              getConfig('REACT_APP_GAME_START') || "",
              { gameId: get().gameId, sessionId: get().sessionId }
            );
            data = response.data;

            if (data) {
              window.localStorage.setItem("BH:Data", JSON.stringify(data));
            }
          }
          // END_DEVTOOL

          _isEndCalled = false;
          _isLastHand = false;

          set({
            gameState: data.state,
            date: data.createdAt,
            buyIns: data.buyIns || [],
          });
          if (data.state === GameState.UnfinishedGame) {
            const configId = data.configId ?? data.details.configId;
            set({
              ...data.details,
              ...data,
              buyIn: {
                configId,
                buyIn: data.buyIn || data.buyIns?.find((buyIn: BuyIn) => buyIn.configId === configId)?.buyIn,
              },
            });
          }
        } catch (err: any) {
          if (err.response) {
            alert('setGame: ' + err.response.data.message?.join("\n"));
          } else console.error(err);
        }
      },
      setBuyIn: (buyIn: BuyIn) => {
        set({ buyIn });
      },
      setReturnUrl: (returnUrl: string) => {
        set({ returnUrl });
      },
      setBet: async () => {
        const sessionId = get().sessionId;
        const buyIn = get().buyIn;

        // Return false if buyIn is not set. For some reasons, this function is ALWAYS called on the game page.
        if (!buyIn?.configId) {
          return false;
        }

        const params = {
          sessionId,
          configId: buyIn.configId,
          amount: buyIn.buyIn,
          cashAmount: buyIn.buyIn,
          bonusAmount: 0,
          currencyCode: get().currencyCode,
        };
        if (window.localStorage.getItem("BH:UseRecording") === "true" && get().enableDebug) return true;

        try {
          const { data } = await axios.post(
            getConfig('REACT_APP_GAME_BET') || "",
            params
          );

          // Append setBetData to recording and mark it as unfinished game
          const dataStored = window.localStorage.getItem("BH:Data");
          if (dataStored) {
            const parsedData = JSON.parse(dataStored);
            window.localStorage.setItem(
              "BH:Data",
              JSON.stringify({
                ...parsedData,
                details: data,
                state: GameState.UnfinishedGame,
              })
            );
          }

          set({ ...data });

          return true;
        } catch (err: any) {
          console.log(err);
          throw err;
        }
      },
      resetHand: (data) =>
        set({
          street: initialState.street,
          showHandId: initialState.showHandId,
          showPrize: initialState.showPrize,
          showBank: initialState.showBank,
          showBounty: initialState.showBounty,
          hideState: initialState.hideState,
          currentId: initialState.currentId,
          skip: initialState.skip,
          currencyCode: initialState.currencyCode,
          ...data,
        }),
      setEnd: async () => {
        const playerId = get().playerId;
        const tournamentId = get().tournamentId;
        const sessionId = get().sessionId;
        const successEnd = get().successEnd;

        if (successEnd || _isEndCalled) {
          return;
        }
        _isEndCalled = true;

        // Set to end if recording is on
        if (window.localStorage.getItem("BH:UseRecording") === "true" && get().enableDebug) {
          set({ successEnd: true });
          window.confirm("Recording end");
          window.location.reload();

          return;
        }

        try {
          const { data } = await axios.post(
            getConfig('REACT_APP_GAME_END') || "",
            { sessionId, playerId, tournamentId }
          );
          console.log(data);
        } catch (err: any) {
          console.error('setEnd: ' + err.response?.data);
        }
        set({ successEnd: true });
      },
      setStartingPlayer: () => {
        const summary = get().summary;
        const players: PlayerRecord = {
          [get().playerId]: {
            ...initialPlayer,
            action: PokerAction.Ante,
            balance: Number(summary?.startingBalance) || 0,
          },
        };
        set({ players });
      },
      nextHand: () => {
        const hands = get().hands;

        if (!hands) return;

        _handCount = Math.max(get().skip ? 0 : -1, get().handCount);
        let isFold = false;

        do {
          _handCount++;
          if (_handCount >= hands.length || !hands[_handCount]) {
            _isLastHand = true;
          } else {
            const roundDetails = hands[_handCount].roundDetails;
            const filtered = (
              roundDetails[PokerAction.Ante] || roundDetails[Street.PreFlop]
            ).filter((p) => p.playerId === get().playerId);
            isFold = !filtered.length
              ? true
              : filtered.slice(-1)[0]?.action === PokerAction.Fold;
          }
        } while (
          !_isLastHand &&
          (!hands[_handCount]?.communityCards.length || isFold)
        );

        set({ skip: initialState.skip });
      },
      setPlayers: () => {
        if (_isLastHand) return;

        const hand = get().hands[_handCount];

        const players = hand.playerDetails.reduce((acc, cur) => {
          const action =
            getDetails(hand.roundDetails[initialState.street], cur.playerId)
              ?.action !== PokerAction.Fold
              ? PokerAction.Ante
              : "";
          return {
            ...acc,
            [cur.playerId]: {
              ...initialPlayer,
              action,
              balance: Number(cur.openingBalance),
              holeCards: cur.holeCards,
              time: 1,
              seatPosition: SeatPosition[cur.playerPosition],
            },
          };
        }, {} as PlayerRecord);

        get().resetHand({ handCount: _handCount, players });
      },
      setHandId: () => {
        const hand = get().hands[get().handCount];
        const playerDetail = hand?.playerDetails.find(
          (player) => player.playerId === get().playerId
        );

        const players = { ...get().players };
        Object.keys(players).forEach((playerId: string) => {
          if (playerId !== get().playerId) {
            delete players[playerId];
          } else {
            const balance = playerDetail
              ? Number(playerDetail?.balance)
              : players[playerId].balance;
            players[playerId] = {
              ...players[playerId],
              balance,
              action: !balance ? PokerAction.AllIn : PokerAction.Ante,
              winnings: initialPlayer.winnings,
              winType: initialPlayer.winType,
              amount: initialPlayer.amount,
              equity: initialPlayer.equity,
            };
          }
        });

        if (_isLastHand) {
          set({ players });
          get().setEnd();
        } else {
          get().resetHand({ showHandId: true, handCount: _handCount, players });
        }
      },
      playerCardShown: (id: string) =>
        set((state) => {
          if (state.skip) return;
          state.currentId = id;
        }),
      nextStreet: () =>
        set((state) => {
          if (state.skip) return;
          switch (state.street) {
            case Street.PreFlop:
              state.street = Street.Flop;
              break;
            case Street.Flop:
              state.street = Street.Turn;
              break;
            case Street.Turn:
              state.street = Street.River;
              break;
            default:
              state.street = Street.PreFlop;
              break;
          }
        }),
      updateAction: () =>
        set((state) => {
          const hand = state.hands[state.handCount];
          if (!hand || state.skip) return;

          _allInPlayers = {};
          const balance = Math.max(
            ...Object.keys(state.players).map((id) => {
              const action = getDetails(hand.roundDetails[state.street], id)?.action;
              return !action || action === PokerAction.Fold ? 0 : state.players[id].balance;
            })
          );
          const highBb = getBb(balance, hand.bigBlind);

          const roundDetails: StreetDetails[] = [
            ...hand.roundDetails[PokerAction.Ante],
            ...hand.roundDetails[state.street],
          ];
          roundDetails
            .filter(
              (p: StreetDetails) => p.action === PokerAction.AllIn || p.isAllIn
            )
            .forEach((p: StreetDetails) => {
              _allInPlayers[p.playerId] = { ...state.players[p.playerId] };
            });

          // check if there are more than one all-in player with similar high stack
          const hasEqual =
            Object.values(_allInPlayers).filter(
              (p) => getBb(p.balance, hand.bigBlind) === highBb
            ).length > 1;

          _hasAllIn = false;

          Object.keys(_allInPlayers).forEach((id: string) => {
            // change action to all-in for players that are lower than highest BB or hasEqual
            const bb = getBb(_allInPlayers[id].balance, hand.bigBlind);
            if (bb < highBb || hasEqual) {
              _allInPlayers[id].action = PokerAction.AllIn;
              state.players[id] = { ..._allInPlayers[id] };
              _hasAllIn = true;
            }
          });
        }),
      updatePlayers: (dealerCards: CardType[]) => {
        const hand = get().hands[get().handCount];
        if (!hand || get().skip) return;

        setTimeout(
          () => {
            set((state) => {
              const roundDetails: StreetDetails[] = [...hand.roundDetails[state.street]];

              const results = calculatePokerOdds({
                players: Object.values(state.players).filter(player => player.action !== PokerAction.Fold && player.action !== '').map(player => ({ seatIndex: player.seatPosition, playerCards: player.holeCards })),
                dealerCards,
              });

              Object.keys(state.players).forEach((playerId: string) => {
                const streetDetails = roundDetails.filter(
                  (p) => p.playerId === playerId
                );
                const player = { ...state.players[playerId] };
                if (!!streetDetails.length) {
                  // update player's action if it's not All In or didn't fold pre-flop
                  const action = streetDetails.slice(-1)[0].action;
                  if (!!player.action && ![player.action, action].includes(PokerAction.AllIn)) {
                    player.action = action;
                  }

                  player.amount = streetDetails.reduce(
                    (total: number, p: StreetDetails) =>
                      total + Number(p.amount),
                    Math.max(0, player.amount)
                  );
                }
                player.time = Date.now();
                player.equity = results?.players.find(p => p.seatIndex === player.seatPosition)?.result.equity ?? initialPlayer.equity;
                state.players[playerId] = { ...player };
              });

              // adjust player's bet amount to the 2nd highest bet
              const amount = Object.values(state.players)
                .map((p) => p.amount)
                .sort((a, b) => b - a)[1];

              Object.values(state.players).forEach((p) => {
                if (p.amount > amount) p.amount = amount;
              });
            });
          },
          _hasAllIn ? 1000 : 0
        );
      },
      updateWinnings: () =>
        setTimeout(() => {
          set((state) => {
            const hand = state.hands[state.handCount];
            if (!hand || state.skip) return;
            Object.keys(state.players).forEach((playerId: string) => {
              const player = { ...state.players[playerId] };
              const playerDetail = hand.playerDetails.find(
                (player) => playerId === player.playerId
              );
              if (state.street === Street.River) player.winType = playerDetail?.winType || initialPlayer.winType;
              player.winnings = playerDetail?.winnings || initialPlayer.winnings;
              state.players[playerId] = { ...player };
            });
          });
        }, 500),
      updatePrize: () =>
        set((state) => {
          state.showPrize = !state.skip;
        }),
      updateBank: () =>
        set((state) => {
          state.showBank = true;
        }),
      updateBounty: () =>
        set((state) => {
          state.showBounty = true;
        }),
      hideHand: () =>
        set({
          hideState: get().skip
            ? 1
            : Math.ceil(
              Math.random() * (Object.keys(get().players).length < 3 ? 2 : 3)
            ),
        }),
      updateBalance: () => {
        set((state) => {
          state.players[state.playerId].time = -1;
        });
      },
      skipHand: () => {
        set({ skip: true });
      },
      reset: () => {
        const currentGameId = get().gameId;
        const currentSessionId = get().sessionId;
        set({
          ...initialState,
          gameId: currentGameId,
          sessionId: currentSessionId,
        });
      },
      getPayouts: async () => {
        try {
          const { data: { distributionConfig } } = await axios.get(
            getConfig('REACT_APP_PAYOUTS') || "",
            {
              params: {
                sessionId: get().sessionId,
                gameId: get().gameId
              },
            }
          );

          set({ payouts: distributionConfig });
        } catch (err: any) {
          console.log(err);
        }
      },
      setAlertMessage: (message?: string) =>
        set((state) => {
          state.alertMessage = message;
        }),
      setShowScratchCard: (show: boolean) =>
        set((state) => {
          state.showScratchCard = show;
        }),
      toggleShowScratchCard: () =>
        set((state) => {
          state.showScratchCard = !state.showScratchCard;
        }),
      setShowRegConfirm: (show: boolean) =>
        set((state) => {
          state.showRegConfirm = show;
        }),
      setShowInsufficient: (show: boolean) =>
        set((state) => {
          state.showInsufficient = show;
        }),
    })),
    {
      name: "bounty-hunter",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        sessionId: state.sessionId,
        gameId: state.gameId,
      }),
    }
  ),
  {
    name: "bh:store",
    enabled: process.env.NODE_ENV === "development",
    trace: true,
  })
);
